/**
 * Invoice Service - Web App Stub
 * 
 * This is a temporary stub service that provides mock data
 * to keep the UI functional while we implement the real service.
 * 
 * TODO: Replace with real DatabaseService integration (Task 4)
 */

// Mock invoice data
const mockInvoices = [
  {
    id: 'inv_001',
    invoiceNumber: 'INV-2026-0001',
    clientId: 'client_001',
    clientName: 'Sample Client',
    date: '2026-04-01',
    dueDate: '2026-04-30',
    status: 'unpaid',
    items: [
      {
        id: 'item_001',
        description: 'Sample Service',
        quantity: 1,
        unitPrice: 1000,
        tax: 180,
        total: 1180
      }
    ],
    subtotal: 1000,
    tax: 180,
    total: 1180,
    notes: 'Sample invoice',
    createdAt: '2026-04-01T10:00:00Z',
    updatedAt: '2026-04-01T10:00:00Z'
  }
];

class InvoiceService {
  /**
   * Get all invoices
   * @returns {Promise<Array>} List of invoices
   */
  async getAll() {
    // TODO: Replace with DatabaseService.getInvoices()
    console.warn('[InvoiceService] Using mock data - implement real service');
    return Promise.resolve([...mockInvoices]);
  }

  /**
   * Get invoice by ID
   * @param {string} id - Invoice ID
   * @returns {Promise<Object>} Invoice object
   */
  async getById(id) {
    // TODO: Replace with DatabaseService.getInvoice(id)
    console.warn('[InvoiceService] Using mock data - implement real service');
    const invoice = mockInvoices.find(inv => inv.id === id);
    if (!invoice) {
      throw new Error(`Invoice ${id} not found`);
    }
    return Promise.resolve({ ...invoice });
  }

  /**
   * Create new invoice
   * @param {Object} invoiceData - Invoice data
   * @returns {Promise<Object>} Created invoice
   */
  async create(invoiceData) {
    // TODO: Replace with DatabaseService.createInvoice(invoiceData)
    console.warn('[InvoiceService] Using mock data - implement real service');
    const newInvoice = {
      id: `inv_${Date.now()}`,
      ...invoiceData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    mockInvoices.push(newInvoice);
    return Promise.resolve({ ...newInvoice });
  }

  /**
   * Update existing invoice
   * @param {string} id - Invoice ID
   * @param {Object} invoiceData - Updated invoice data
   * @returns {Promise<Object>} Updated invoice
   */
  async update(id, invoiceData) {
    // TODO: Replace with DatabaseService.updateInvoice(id, invoiceData)
    console.warn('[InvoiceService] Using mock data - implement real service');
    const index = mockInvoices.findIndex(inv => inv.id === id);
    if (index === -1) {
      throw new Error(`Invoice ${id} not found`);
    }
    mockInvoices[index] = {
      ...mockInvoices[index],
      ...invoiceData,
      updatedAt: new Date().toISOString()
    };
    return Promise.resolve({ ...mockInvoices[index] });
  }

  /**
   * Delete invoice
   * @param {string} id - Invoice ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    // TODO: Replace with DatabaseService.deleteInvoice(id)
    console.warn('[InvoiceService] Using mock data - implement real service');
    const index = mockInvoices.findIndex(inv => inv.id === id);
    if (index === -1) {
      throw new Error(`Invoice ${id} not found`);
    }
    mockInvoices.splice(index, 1);
    return Promise.resolve();
  }

  /**
   * Search invoices
   * @param {string} query - Search query
   * @returns {Promise<Array>} Matching invoices
   */
  async search(query) {
    // TODO: Replace with DatabaseService query
    console.warn('[InvoiceService] Using mock data - implement real service');
    const lowerQuery = query.toLowerCase();
    const results = mockInvoices.filter(inv => 
      inv.invoiceNumber.toLowerCase().includes(lowerQuery) ||
      inv.clientName.toLowerCase().includes(lowerQuery)
    );
    return Promise.resolve(results);
  }

  /**
   * Generate PDF for invoice
   * @param {string} id - Invoice ID
   * @returns {Promise<Blob>} PDF blob
   */
  async generatePDF(id) {
    // TODO: Implement PDF generation using jsPDF or pdfmake
    console.warn('[InvoiceService] PDF generation not yet implemented');
    throw new Error('PDF generation not yet implemented');
  }

  /**
   * Get active invoice theme/settings
   * @returns {Promise<Object>} Invoice theme settings
   */
  async getActiveTheme() {
    try {
      // Import settings service
      const { default: settingsService } = await import('./settings.service');
      
      // Get theme from settings service
      const theme = await settingsService.getActiveTheme();
      
      return theme;
    } catch (error) {
      console.error('[InvoiceService] Error fetching theme:', error);
      
      // Fallback to default theme
      return {
        company: {
          name: 'Sample Company',
          address: '123 Main St',
          city: 'Mumbai',
          state: 'Maharashtra',
          pincode: '400001',
          gstin: '27XXXXX1234X1Z5',
          phone: '555-0100',
          email: 'info@example.com'
        },
        brandColor: '#6366f1',
        accentColor: 'indigo',
        fontFamily: 'Inter',
        showGstBreakdown: true,
        showHsn: true,
        showQuantityUom: true,
        showItemDescription: true,
        showAmountInWords: true,
        footerText: 'This is a computer-generated invoice and does not require a signature.',
        logoUrl: null
      };
    }
  }
}

// Export singleton instance
export const invoiceService = new InvoiceService();
export default invoiceService;
